/**
 * Puts a Sims-style character into the real room and lets you click to move it.
 *
 * Wiring notes:
 *
 * - The actor is added to `interiorContainer`, the same sortable container the
 *   furniture markers live in, so one `zIndex` scale orders both. Putting it in
 *   its own container would force the whole room to be re-parented to keep depth
 *   sorting working.
 * - The walk grid is rebuilt from `computeFootprintTileMap()` whenever the
 *   furniture render pass changes, so moving a sofa immediately changes where
 *   the character may walk.
 * - The floor already has `eventMode: 'static'` and its own long-press handling;
 *   this only *adds* a `pointertap` listener and never removes or replaces the
 *   existing ones.
 */
import { useEffect, useRef } from 'react';
import type { MutableRefObject } from 'react';
import * as PIXI from 'pixi.js';

import { mapSceneSingleton } from '../map/mapScene';
import { localToTile, tileToWorld } from '../../domain/grid';
import { computeFootprintTileMap } from '../../domain/depthMath';
import { readFurnitureData } from '../../domain/sceneNode';
import type { RoomPixiRoots } from './useRoomScene';
import type { RoomSceneState } from './state/roomSceneState';
import { CharacterActor } from './character/actor';
import { loadRecipe, type CharacterRecipe } from './character/atlas';
import { FloorTapGestureGuard } from './character/floorTapGesture';
import {
  buildWalkGrid,
  findPath,
  isWalkable,
  nearestWalkable,
  restingTileFor,
  simplifyPath,
  REST_CLEARANCE_TILES,
  type PathRequest,
  type TileXY,
} from './character/pathfinding';

/**
 * The vendor's own character, unmodified: `body_appearance_a` is the author's
 * textured body — its outfit is painted into the skin — plus one hair mesh.
 * `top` / `bottom` stay null on purpose: the separate garment meshes in the
 * pack were generated by an earlier agent and read as broken clothing, and a
 * garment layer over an already-clothed body double-draws the torso.
 */
export const DEFAULT_RECIPE: CharacterRecipe = {
  body: 'body_appearance_a',
  top: null,
  bottom: null,
  hair: 'hair_hair_buns_01',
};

/**
 * Preferred spawn, in sub-tile coordinates: open floor towards the front of the
 * room. It is only a preference — the real spawn is the nearest tile that is
 * walkable *and* has walkable room around it, because a fixed tile lands under
 * the coffee table in about half the rooms people actually build.
 */
const SPAWN_PREFERENCE: TileXY = { tileX: 30, tileY: 30 };

/**
 * The spawn is a resting tile like any other, so it uses the same rule: the
 * roomier grid first (the character wakes up with daylight around it), the
 * plain walkable grid only if the room is too cluttered to offer one. This used
 * to hand-roll its own "walkable at ±2 on both axes" cross, which is a coarser
 * and differently-shaped test than the clearance grid the walk itself uses.
 */
function findSpawn(grid: number[][], restGrid: number[][], prefer: TileXY): TileXY {
  return (
    nearestWalkable(restGrid, prefer, 16) ?? nearestWalkable(grid, prefer, 20) ?? prefer
  );
}

export interface UseRoomCharacterParams {
  enabled: boolean;
  isSceneReady: boolean;
  mapObjectId: number;
  recipe?: CharacterRecipe;
  pixiRootsRef: MutableRefObject<RoomPixiRoots>;
  sceneRef: MutableRefObject<RoomSceneState>;
}

export function useRoomCharacter({
  enabled,
  isSceneReady,
  mapObjectId,
  recipe = DEFAULT_RECIPE,
  pixiRootsRef,
  sceneRef,
}: UseRoomCharacterParams): void {
  const actorRef = useRef<CharacterActor | null>(null);
  const gridRef = useRef<number[][] | null>(null);
  // The same room inflated by the resting clearance. Kept beside `gridRef` and
  // rebuilt in lockstep with it: a stale copy of either one would put the
  // character inside furniture, so they are never written apart.
  const restGridRef = useRef<number[][] | null>(null);
  const pathRequestRef = useRef<PathRequest | null>(null);

  useEffect(() => {
    if (!enabled || !isSceneReady) return;
    const roots = pixiRootsRef.current;
    const interior = roots.interiorContainer;
    const floor = sceneRef.current.sprites.floor;
    const viewport = roots.roomViewport;
    const app = roots.roomViewport?.parent
      ? (roots.roomViewport.parent as PIXI.Container & { renderer?: unknown })
      : null;
    if (!interior || !floor || !viewport) return;

    let cancelled = false;
    let ticker: PIXI.Ticker | null = null;
    let watchdog: ReturnType<typeof setInterval> | null = null;
    let lastRenderPass = -1;
    const floorTapGesture = new FloorTapGestureGuard();

    const onViewportMoved = () => {
      floorTapGesture.viewportMoved();
    };
    const onViewportMovedEnd = () => {
      floorTapGesture.viewportMoveEnded();
    };
    const onViewportPointerDown = (event: PIXI.FederatedPointerEvent) => {
      // `global` is screen space here (and CSS-pixel-sized with this app's
      // autoDensity renderer), so the swipe threshold does not change with zoom.
      floorTapGesture.pointerDown(event.pointerId, event.global);
    };
    const onViewportPointerUp = (event: PIXI.FederatedPointerEvent) => {
      floorTapGesture.pointerUp(event.pointerId);
    };
    const onViewportPointerCancelled = (event: PIXI.FederatedPointerEvent) => {
      floorTapGesture.pointerCancelled(event.pointerId);
    };
    const onViewportPointerTap = (event: PIXI.FederatedPointerEvent) => {
      // A floor tap is consumed by `onFloorTap` before it bubbles here. This
      // clears records for taps whose target was another room object.
      floorTapGesture.finishTap(event.pointerId);
    };

    // Public viewport lifecycle events are preferable to reading a plugin's
    // velocity fields. Capture-phase pointer listeners see every finger in a
    // gesture without preventing the viewport's own drag/pinch handlers.
    viewport.on('moved', onViewportMoved);
    // `moved-end` only fires when the viewport's position changed; a pure zoom
    // ends with `zoomed-end`, so both are needed to clear the moving latch.
    viewport.on('moved-end', onViewportMovedEnd);
    viewport.on('zoomed-end', onViewportMovedEnd);
    viewport.addEventListener('pointerdown', onViewportPointerDown, { capture: true });
    viewport.addEventListener('pointerup', onViewportPointerUp, { capture: true });
    viewport.addEventListener('pointercancel', onViewportPointerCancelled, { capture: true });
    viewport.on('pointerupoutside', onViewportPointerCancelled);
    viewport.on('pointertap', onViewportPointerTap);

    const onTick = (t: PIXI.Ticker) => {
      const actor = actorRef.current;
      if (!actor) return;
      actor.update(t.deltaMS / 1000);
    };

    const rebuildGrid = () => {
      gridRef.current = buildWalkGrid(sceneRef.current.furniture.markers);
      restGridRef.current = buildWalkGrid(
        sceneRef.current.furniture.markers,
        REST_CLEARANCE_TILES,
      );
      if (import.meta.env.DEV) {
        // Dev-only probe, same contract as `__SIMS_CHARACTER` in actor.ts: it
        // lets the QA harness draw the walkability the character actually used
        // over a real frame, so "he is standing in the sofa" can be settled as
        // either a wrong grid or a sprite that merely overlaps a legal tile.
        (window as unknown as Record<string, unknown>).__SIMS_WALKGRID = {
          grid: gridRef.current,
          footprints: Array.from(
            computeFootprintTileMap(sceneRef.current.furniture.markers).entries(),
          ).map(([key, furnitureId]) => ({ key, furnitureId })),
          // Projection taken from the live interior container, so an overlay
          // cannot drift relative to the game the way a re-derived lattice can.
          // Per-item artwork extent vs declared footprint. When a sofa's sprite
          // covers tiles its footprint does not claim, the character legally
          // stands on free floor yet is drawn inside the sofa.
          probeFurniture: () =>
            Array.from(sceneRef.current.furniture.markers.entries()).map(([id, marker]) => {
              const b = marker.getBounds();
              // Metadata lives on the first child sprite, with the marker only
              // as fallback — same precedence the depth math uses.
              const data = readFurnitureData(marker, marker.children[0] as never);
              return {
                id,
                category: data?.furniture_catalog?.category ?? null,
                footprint: data?.furniture_catalog?.footprint_tiles ?? null,
                bounds: { x: b.x, y: b.y, width: b.width, height: b.height },
                // The depth audit compares this against the character's own
                // zIndex, so it has to be the number the renderer sorted by —
                // not one the harness re-derives from the same formula it is
                // supposed to be testing.
                z: marker.zIndex,
                parent: marker.parent?.label ?? null,
              };
            }),
          // Nudge the whole room by a screen-pixel delta — what a camera pan
          // does to every sprite at once. The furniture shimmer probe needs a
          // move it can ask for in exact screen pixels (including halves),
          // which a synthesised drag gesture cannot promise.
          panScreen: (dxPx: number, dyPx: number) => {
            const o = interior.toGlobal(new PIXI.Point(0, 0));
            const u = interior.toGlobal(new PIXI.Point(1, 0));
            const scale = Math.hypot(u.x - o.x, u.y - o.y) || 1;
            interior.x += dxPx / scale;
            interior.y += dyPx / scale;
          },
          tileToScreen: (tileX: number, tileY: number) => {
            const w = tileToWorld(tileX, tileY);
            const p = interior.toGlobal(new PIXI.Point(w.x, w.y));
            return [p.x, p.y];
          },
          // Same reason as `__SIMS_CHARACTER.setVisible`: `bounds` above is the
          // sprite's frame, and a sofa's frame is padded well past the floor it
          // owns. Hiding one item and diffing two captured frames yields the
          // pixels that item actually paints, which is the only extent a critic
          // looking at a screenshot can be judging.
          setFurnitureVisible: (id: number, v: boolean) => {
            const marker = sceneRef.current.furniture.markers.get(id);
            if (marker) marker.visible = v;
          },
          // Which display object a screen point belongs to. A refused walk
          // order has two very different causes — the pathfinder said no, or
          // the tap never reached the floor because a wall or a sofa is on
          // top — and from outside the page they look identical.
          hitTest: (x: number, y: number) => {
            // The room reuses the map's PIXI application (see `useRoomScene`),
            // so the event system to ask lives there — the viewport's parent is
            // only the stage and carries no renderer.
            const renderer = mapSceneSingleton.app?.renderer;
            if (!renderer) return null;
            const hit = renderer.events.rootBoundary.hitTest(x, y);
            if (!hit) return null;
            const chain: string[] = [];
            for (let n: PIXI.Container | null = hit; n; n = n.parent) {
              chain.push(n.label ?? n.constructor.name);
            }
            // Identity against the live floor sprite, not a label match: labels
            // are optional in PIXI and every unnamed container reports the same
            // constructor name, so a name test cannot tell the floor from the
            // sofa drawn on it.
            return {
              label: hit.label ?? hit.constructor.name,
              chain,
              isFloor: hit === (sceneRef.current.sprites.floor as unknown as PIXI.Container),
            };
          },
          // Frames of a walk, read straight out of the renderer.
          //
          // A screenshot taken through the automation driver costs ~500 ms
          // round-trip, while crossing four tiles takes under two seconds — so
          // sampling a walk that way yields two or three frames and the
          // silhouette QA ends up judging a gait it never saw. Extracting from
          // the renderer instead leaves the game running at its real speed and
          // samples once per rendered frame. The character's logical state is
          // read in the same tick as the pixels, so a frame can never be
          // labelled with a tile the actor had already left.
          grabFrames: async (
            count: number,
            rect: { x: number; y: number; width: number; height: number },
          ) => {
            const app = mapSceneSingleton.app;
            if (!app) return [];
            const probe = () =>
              (window as unknown as Record<string, Record<string, unknown>>).__SIMS_CHARACTER;
            const frame = new PIXI.Rectangle(rect.x, rect.y, rect.width, rect.height);
            // PNG encoding is what makes a capture expensive — on the software
            // GL of a headless run it costs longer than a whole tile of walking,
            // so encoding inside the loop still starved the sample (one or two
            // frames per leg). Only the raw readback happens while he walks;
            // the frames are turned into images afterwards, when nothing about
            // the gait depends on how long it takes.
            const raw: { px: ImageData; meta: Record<string, unknown>; dt: number }[] = [];
            for (let i = 0; i < count; i++) {
              await new Promise((r) => requestAnimationFrame(r));
              const before = probe();
              // Stop as soon as the leg is over: trailing idle frames would
              // dilute the very measurement this capture exists for.
              if (i > 0 && !before?.walking) break;
              const t0 = performance.now();
              const shot = app.renderer.extract.pixels({ target: app.stage, frame });
              raw.push({
                px: new ImageData(
                  new Uint8ClampedArray(shot.pixels),
                  shot.width,
                  shot.height,
                ),
                dt: performance.now() - t0,
                meta: {
                  tileX: before?.tileX ?? null,
                  tileY: before?.tileY ?? null,
                  facing: before?.facing ?? null,
                  heading: before?.heading ?? null,
                  walking: before?.walking ?? false,
                  turning: before?.turning ?? false,
                },
              });
            }
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            return raw.map(({ px, meta, dt }) => {
              canvas.width = px.width;
              canvas.height = px.height;
              ctx?.putImageData(px, 0, 0);
              return { ...meta, dt, png: canvas.toDataURL('image/png') };
            });
          },
        };
      }
    };

    /**
     * Furniture markers appear over several frames after `isSceneReady`, so a
     * grid built the moment the scene is ready sees an empty room and calls
     * every tile walkable — which spawned the character inside the coffee table.
     * Wait for the first markers before spawning, and rebuild whenever the
     * furniture render pass moves.
     */
    const waitForFurniture = async (timeoutMs = 15000) => {
      const started = Date.now();
      let stable = 0;
      let last = -1;
      while (!cancelled && Date.now() - started < timeoutMs) {
        const size = sceneRef.current.furniture.markers.size;
        // A fixed grace period is not enough — markers keep arriving, and one
        // that lands after the grid is built is furniture the character does not
        // know about. Wait for the count to stop growing instead.
        stable = size > 0 && size === last ? stable + 1 : 0;
        last = size;
        if (stable >= 3) return true;
        await new Promise((r) => setTimeout(r, 120));
      }
      return last > 0;
    };

    /** Move the actor off a tile that furniture occupies. */
    const nudgeIfBlocked = () => {
      const grid = gridRef.current;
      const a = actorRef.current;
      if (!grid || !a || a.isWalking) return;
      const here = { tileX: Math.round(a.tileX), tileY: Math.round(a.tileY) };
      if (isWalkable(grid, here)) return;
      const free = nearestWalkable(grid, here, 12);
      if (free) {
        a.tileX = free.tileX;
        a.tileY = free.tileY;
      }
    };

    const onFloorTap = (event: PIXI.FederatedPointerEvent) => {
      if (!floorTapGesture.consumeValidTap(event.pointerId, event.global)) return;
      const actor = actorRef.current;
      if (!actor) return;
      // Never steal a tap that belongs to another mode.
      if (sceneRef.current.drag.state) return;
      if (sceneRef.current.admin.tileMode) return;

      const local = interior.toLocal(event.global);
      const target = localToTile(local);
      rebuildGrid();
      const grid = gridRef.current;
      const restGrid = restGridRef.current;
      if (!grid || !restGrid) return;

      // Where he *stops* is what the eye judges: a tile that is merely legal
      // leaves the standing sprite's foot overlapping the neighbouring table's
      // art. Ask for a roomier tile first and settle for the legal one only if
      // the room offers nothing better.
      const goal = restingTileFor(grid, restGrid, target);
      if (!goal) return;

      const from = {
        tileX: Math.round(actor.tileX),
        tileY: Math.round(actor.tileY),
      };
      // The actor may be standing on a tile that a newly placed item now blocks;
      // step it to the nearest free tile rather than refusing to path at all.
      const start = nearestWalkable(grid, from) ?? from;

      pathRequestRef.current?.cancel();
      pathRequestRef.current = findPath(grid, start, goal, (path) => {
        pathRequestRef.current = null;
        if (cancelled || !path || path.length === 0) return;
        actor.setPath(simplifyPath(path));
      });
    };

    void (async () => {
      try {
        const sheets = await loadRecipe(recipe);
        if (cancelled) return;

        await waitForFurniture();
        if (cancelled) return;

        rebuildGrid();
        const spawn =
          gridRef.current && restGridRef.current
            ? findSpawn(gridRef.current, restGridRef.current, SPAWN_PREFERENCE)
            : SPAWN_PREFERENCE;

        const actor = new CharacterActor({
          sheets,
          tileX: spawn.tileX,
          tileY: spawn.tileY,
          facing: 's',
          getFurnitureMarkers: () => sceneRef.current?.furniture.markers ?? new Map(),
        });
        actorRef.current = actor;
        interior.addChild(actor.container);

        // Re-derive the grid once more now that the actor exists, and correct
        // the spawn synchronously. Leaving this to the interval below meant the
        // character stood inside the coffee table for its first half second.
        rebuildGrid();
        nudgeIfBlocked();

        floor.on('pointertap', onFloorTap);

        ticker = PIXI.Ticker.shared;
        ticker.add(onTick);

        // Moving a sofa changes where the character may walk, and can strand it
        // on a tile that just became furniture. Re-derive both on every render
        // pass rather than only on tap.
        // -1, not the current pass: the first tick must always rebuild. Waiting
        // for the pass to *change* means that if the furniture batch finished
        // landing between the spawn and here, the grid the character spawned
        // against is never re-examined — which left it standing on the coffee
        // table for the first two frames of the recording.
        lastRenderPass = -1;
        watchdog = setInterval(() => {
          const current = sceneRef.current.furniture.renderPass;
          if (current === lastRenderPass) return;
          lastRenderPass = current;
          rebuildGrid();
          nudgeIfBlocked();
        }, 500);
      } catch (err) {
        // A missing atlas must not take the room down with it.
        console.warn('[character] failed to load', err);
      }
    })();

    return () => {
      cancelled = true;
      pathRequestRef.current?.cancel();
      pathRequestRef.current = null;
      if (watchdog) clearInterval(watchdog);
      ticker?.remove(onTick);
      floor.off('pointertap', onFloorTap);
      viewport.off('moved', onViewportMoved);
      viewport.off('moved-end', onViewportMovedEnd);
      viewport.off('zoomed-end', onViewportMovedEnd);
      viewport.removeEventListener('pointerdown', onViewportPointerDown, { capture: true });
      viewport.removeEventListener('pointerup', onViewportPointerUp, { capture: true });
      viewport.removeEventListener('pointercancel', onViewportPointerCancelled, { capture: true });
      viewport.off('pointerupoutside', onViewportPointerCancelled);
      viewport.off('pointertap', onViewportPointerTap);
      actorRef.current?.destroy();
      actorRef.current = null;
      gridRef.current = null;
      restGridRef.current = null;
      void app;
    };
  }, [enabled, isSceneReady, mapObjectId, recipe, pixiRootsRef, sceneRef]);
}
